import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  useCreateOrganizationDomainMutation,
  useUpdateOrganizationDomainMutation,
} from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  DomainFormModalProps,
  DomainFormValues,
} from "@/features/organizations/profile/types";

import "../styles.css";

const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

const DomainFormModal = ({
  open,
  setOpen,
  organization,
  domain,
}: DomainFormModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const isEdit = !!domain;
  const [createOrganizationDomain] = useCreateOrganizationDomainMutation();
  const [updateOrganizationDomain] = useUpdateOrganizationDomainMutation();

  const domainSchema: yup.ObjectSchema<DomainFormValues> = yup.object().shape({
    domain: yup
      .string()
      .trim()
      .required(t("organizations.validation.required"))
      .matches(DOMAIN_REGEX, t("organizations.validation.notValid")),
  });

  const form = useForm<DomainFormValues>({
    resolver: yupResolver(domainSchema),
    mode: "onChange",
    defaultValues: { domain: domain?.domain ?? "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ domain: domain?.domain ?? "" });
    }
  }, [open]);

  const onClose = () => setOpen(false);

  const handleSubmit = (values: DomainFormValues) => {
    const request = domain
      ? updateOrganizationDomain({ id: domain.id, domain: values.domain })
      : createOrganizationDomain({ domain: values.domain, organization });

    request
      .unwrap()
      .then(() => {
        notification.success({
          message: isEdit
            ? t("organizations.labels.domainUpdated")
            : t("organizations.labels.domainAdded"),
        });
        onClose();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <BaseModal
      destroyOnClose
      title={
        isEdit
          ? t("organizations.labels.editDomain")
          : t("organizations.labels.addDomain")
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="modal-footer-buttons">
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={onClose}
          >
            {t("organizations.labels.cancel")}
          </BaseButton>

          <BaseButton
            variant={BUTTON_VARIANTS.PRIMARY}
            size={BUTTON_SIZES.SMALL}
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {isEdit
              ? t("organizations.labels.save")
              : t("organizations.labels.addDomain")}
          </BaseButton>
        </div>
      }
    >
      <div className="form-input-container">
        <FormFieldWrapper label={t("organizations.labels.domain")} required>
          <HookFormInput
            name="domain"
            control={form.control}
            placeholder={t("organizations.placeholders.enterDomain")}
          />
        </FormFieldWrapper>
      </div>
    </BaseModal>
  );
};

export default DomainFormModal;
